// M2 (Carla, 2026-10-12): the invoice's Xero sync status, on the invoice.
//
// Nothing at all for someone without Xero (the server answers connected: false). For someone with it:
// where the invoice is, in words; Xero's own refusal word for word when a push failed, at the button
// that retries it; and "Send to Xero" for the person who may change the invoice. A failed status load
// is shown as an error with the server's message, never as "not in Xero".
//
// The push answers 202 at once (several Xero calls; the API has a ~30 s front-door timeout), so the
// panel reads the outcome back from the server rather than assuming it: never a success state from
// a response that has not been read.
import { useState, useEffect, useCallback, useRef } from 'react';
import { api } from '../../api/client';
import { messageFromError } from '../../utils/apiError';
import { formatDateTime } from '../../utils/formatting';
import { formatMoney } from '../../utils/currency';
import { Button, StatusPill, Banner, ErrorState } from '../../ui';

const POLL_MS = 1500;
const POLL_LIMIT = 20;

const PILL = {
  queued: { tone: 'pending', label: 'Waiting' },
  not_sent: { tone: 'neutral', label: 'Not in Xero' },
  pushing: { tone: 'pending', label: 'Sending' },
  pushed: { tone: 'success', label: 'In Xero' },
  part_paid: { tone: 'pending', label: 'Part paid' },
  paid_in_xero: { tone: 'success', label: 'Paid in Xero' },
  mismatch: { tone: 'warning', label: 'Check amount' },
  voided: { tone: 'neutral', label: 'Voided' },
  voided_in_xero: { tone: 'warning', label: 'Voided in Xero' },
  error: { tone: 'warning', label: 'Not sent' },
};

const PAID_HERE = ['paid', 'partially_refunded', 'refunded'];

function describe(sync, invoice) {
  const code = invoice.currency || 'GBP';
  const org = sync.orgName || 'your organisation';
  const when = sync.lastPushedAt ? ` Sent ${formatDateTime(sync.lastPushedAt)}.` : '';
  const paidHere = PAID_HERE.includes(invoice.status);
  const account = sync.paymentAccountName;
  switch (sync.state) {
    case 'queued':
      return paidHere && account
        ? `Goes to ${org} in Xero within 15 minutes, with its payment, recorded against ${account}.`
        : `Goes to ${org} in Xero within 15 minutes.`;
    case 'not_sent':
      if (paidHere) {
        return account
          ? `Not in Xero. If you send it, Aidey records its payment in Xero against ${account}.`
          : 'Not in Xero. If you send it, Xero will show it as owed until you record the payment in Xero. To have Aidey record payments there, choose a bank account in Settings, Invoicing.';
      }
      return invoice.status === 'draft' || invoice.approvalStatus === 'pending'
        ? 'Not in Xero. An invoice goes to Xero once it is sent.'
        : 'Not in Xero. It was sent before Xero was connected, so it goes only if you send it.';
    case 'pushing': return 'Sending to Xero…';
    case 'pushed': return `In Xero as ${sync.xeroNumber || invoice.number}.${when} A payment recorded in Xero marks it paid here.`;
    case 'part_paid': return `In Xero as ${sync.xeroNumber || invoice.number}. Part paid in Xero: ${formatMoney(sync.xeroAmountPaid, code)} of ${formatMoney(sync.xeroTotal, code)}.`;
    case 'paid_in_xero':
      return sync.paymentRecordedAt
        ? `Paid in Xero (${sync.xeroNumber || invoice.number}). Aidey recorded the payment in Xero against ${account || 'the chosen bank account'} on ${formatDateTime(sync.paymentRecordedAt)}.`
        : `Paid in Xero (${sync.xeroNumber || invoice.number}).`;
    case 'mismatch': return `Paid in Xero, but Xero's amount (${formatMoney(sync.xeroTotal, code)}) does not match this invoice, so it has not been marked paid here. Check both, then mark it paid yourself if it is right.`;
    case 'voided': return 'Voided in Xero.';
    case 'voided_in_xero': return 'Voided in Xero. Aidey no longer sends changes to it; cancel it here if it is no longer owed.';
    default: return null;
  }
}

export default function XeroSyncPanel({ invoice, canChange }) {
  const [sync, setSync] = useState(null);
  const [loadError, setLoadError] = useState(null);
  const [pushError, setPushError] = useState(null);
  const [starting, setStarting] = useState(false);
  const polls = useRef(0);
  const alive = useRef(true);
  useEffect(() => () => { alive.current = false; }, []);

  const load = useCallback(async () => {
    try {
      const res = await api(`/api/connections/invoicing/xero/invoices/${invoice.id}`);
      const data = await res.json();
      if (!alive.current) return;
      setSync(data?.sync || { connected: false });
      setLoadError(null);
    } catch (err) {
      if (alive.current) setLoadError(messageFromError(err, 'The Xero status of this invoice could not be loaded.'));
    }
  }, [invoice.id]);

  useEffect(() => { load(); }, [load, invoice.status, invoice.updatedAt]);

  // While a push runs, read the outcome back until it settles (bounded).
  useEffect(() => {
    if (sync?.state !== 'pushing') { polls.current = 0; return undefined; }
    if (polls.current >= POLL_LIMIT) return undefined;
    const t = setTimeout(() => { polls.current += 1; load(); }, POLL_MS);
    return () => clearTimeout(t);
  }, [sync, load]);

  async function push() {
    setStarting(true); setPushError(null);
    try {
      const res = await api(`/api/connections/invoicing/xero/invoices/${invoice.id}/push`, { method: 'POST' });
      const data = await res.json();
      if (data?.sync) setSync(data.sync);
      else await load();
    } catch (err) {
      setPushError(messageFromError(err, 'Sending to Xero could not be started. Try again.'));
    } finally { setStarting(false); }
  }

  if (loadError) {
    return (
      <section className="invc-panel" aria-label="Xero">
        <ErrorState title="Xero status did not load" message={loadError} onRetry={load} />
      </section>
    );
  }
  if (!sync || !sync.connected) return null;
  if (!sync.setUp) {
    return <Banner tone="notice">Xero is connected, but sending invoices to it is not switched on yet.</Banner>;
  }

  const pill = PILL[sync.state] || PILL.not_sent;
  const text = describe(sync, invoice);
  const busy = starting || sync.state === 'pushing';
  const label = sync.state === 'error' ? 'Try again'
    : sync.state === 'queued' ? 'Send to Xero now'
      : sync.state === 'not_sent' ? 'Send to Xero' : 'Send again';
  const offer = canChange && sync.canPush && !['paid_in_xero', 'voided', 'voided_in_xero'].includes(sync.state);
  const retryAt = sync.error?.retryAfter ? formatDateTime(sync.error.retryAfter) : null;

  return (
    <section className="invc-panel" aria-label="Xero" data-testid="xero-sync-panel">
      <div className="invc-panel__head">
        <h3 className="invc-panel__title">Xero</h3>
        <StatusPill tone={pill.tone}>{pill.label}</StatusPill>
      </div>
      {text && <p className="invc-small">{text}</p>}
      {sync.state === 'error' && sync.error?.message && (
        <p className="invc-error" role="alert">
          {sync.error.message}{retryAt ? ` Aidey will try again after ${retryAt}.` : ''}
        </p>
      )}
      {sync.warning && <p className="invc-small" role="status">{sync.warning}</p>}
      {['partially_refunded', 'refunded'].includes(invoice.status) && (
        <p className="invc-small">A refund recorded in Aidey is not sent to Xero. Record it in Xero yourself.</p>
      )}
      {pushError && <p className="invc-error" role="alert">{pushError}</p>}
      {offer && (
        <div className="invc-actions">
          <Button variant="secondary" size="sm" onClick={push} disabled={busy} aria-busy={busy || undefined}>
            {busy ? 'Sending…' : label}
          </Button>
        </div>
      )}
    </section>
  );
}
